import { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Leaf, Mail, Check, AlertCircle } from 'lucide-react';
import { SITE_NAME } from '@/config/site';

interface EmailPrefs {
  categories: {
    product_updates: boolean;
    growing_tips: boolean;
    promotions: boolean;
    seasonal: boolean;
  };
  frequency: 'all' | 'reduced' | 'minimal';
}

const DEFAULT_PREFS: EmailPrefs = {
  categories: { product_updates: true, growing_tips: true, promotions: true, seasonal: true },
  frequency: 'all',
};

export default function EmailPreferences() {
  const { t } = useTranslation('emailPreferences');
  const params = new URLSearchParams(window.location.search);
  const email = params.get('email') || '';
  const token = params.get('token') || '';

  const [prefs, setPrefs] = useState<EmailPrefs>(DEFAULT_PREFS);
  const [firstName, setFirstName] = useState('');
  const [isActive, setIsActive] = useState(true);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState('');

  const apiBase = import.meta.env.VITE_API_URL || '';

  const categories = useMemo(
    () => [
      {
        key: 'product_updates' as const,
        label: t('categories.productUpdatesLabel'),
        description: t('categories.productUpdatesDesc'),
      },
      {
        key: 'growing_tips' as const,
        label: t('categories.growingTipsLabel'),
        description: t('categories.growingTipsDesc'),
      },
      {
        key: 'promotions' as const,
        label: t('categories.promotionsLabel'),
        description: t('categories.promotionsDesc'),
      },
      {
        key: 'seasonal' as const,
        label: t('categories.seasonalLabel'),
        description: t('categories.seasonalDesc'),
      },
    ],
    [t],
  );

  const frequencies = useMemo(
    () => [
      { value: 'all' as const, label: t('frequencies.allLabel'), description: t('frequencies.allDesc') },
      { value: 'reduced' as const, label: t('frequencies.reducedLabel'), description: t('frequencies.reducedDesc') },
      { value: 'minimal' as const, label: t('frequencies.minimalLabel'), description: t('frequencies.minimalDesc') },
    ],
    [t],
  );

  useEffect(() => {
    if (!email || !token) {
      setError(t('invalidLinkMissing'));
      setLoading(false);
      return;
    }

    fetch(`${apiBase}/api/newsletter/preferences?email=${encodeURIComponent(email)}&token=${encodeURIComponent(token)}`)
      .then((r) => r.json())
      .then((data) => {
        if (data.error) {
          setError(data.error);
        } else {
          setPrefs(data.preferences || DEFAULT_PREFS);
          setFirstName(data.first_name || '');
          setIsActive(data.is_active);
        }
      })
      .catch(() => setError(t('loadFailed')))
      .finally(() => setLoading(false));
    // `t` from useTranslation is stable across renders within the same
    // locale; including it would cause this effect to re-fire and double-
    // fetch on locale change, which is the wrong reaction. Locale changes
    // for an opened email-prefs link should be a manual reload.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [email, token, apiBase]);

  async function handleSave() {
    setSaving(true);
    setSaved(false);
    setError('');
    try {
      const res = await fetch(`${apiBase}/api/newsletter/preferences`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, token, preferences: prefs }),
      });
      const data = await res.json();
      if (data.error) {
        setError(data.error);
      } else {
        setSaved(true);
        setTimeout(() => setSaved(false), 3000);
      }
    } catch {
      setError(t('saveFailed'));
    } finally {
      setSaving(false);
    }
  }

  function toggleCategory(key: keyof EmailPrefs['categories']) {
    setPrefs((prev) => ({
      ...prev,
      categories: { ...prev.categories, [key]: !prev.categories[key] },
    }));
    setSaved(false);
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-stone-50 flex items-center justify-center">
        <div className="animate-spin h-8 w-8 border-2 border-sage-700 border-t-transparent rounded-full" />
      </div>
    );
  }

  if (error && !prefs) {
    return (
      <div className="min-h-screen bg-stone-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-xl shadow-sm p-8 max-w-md text-center">
          <AlertCircle className="h-12 w-12 text-red-500 mx-auto mb-4" />
          <h1 className="text-xl font-bold text-stone-900 mb-2">{t('invalidLink')}</h1>
          <p className="text-stone-600">{error}</p>
        </div>
      </div>
    );
  }

  if (!isActive) {
    return (
      <div className="min-h-screen bg-stone-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-xl shadow-sm p-8 max-w-md text-center">
          <Mail className="h-12 w-12 text-stone-400 mx-auto mb-4" />
          <h1 className="text-xl font-bold text-stone-900 mb-2">{t('unsubscribed.heading')}</h1>
          <p className="text-stone-600">{t('unsubscribed.body')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-stone-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-xl shadow-sm w-full max-w-lg">
        {/* Header */}
        <div className="bg-sage-800 rounded-t-xl px-6 py-5 text-center">
          <div className="flex items-center justify-center gap-2 mb-1">
            <Leaf className="h-6 w-6 text-sage-200" />
            <h1 className="text-xl font-bold text-white">{SITE_NAME}</h1>
          </div>
          <p className="text-sage-200 text-sm">{t('title')}</p>
        </div>

        <div className="p-6">
          <p className="text-stone-600 mb-6">
            {firstName ? t('introWithName', { name: firstName }) : t('intro')}
          </p>

          {/* Category toggles */}
          <div className="space-y-3 mb-8">
            <h2 className="text-sm font-semibold text-stone-700 uppercase tracking-wide">
              {t('categoriesHeading')}
            </h2>
            {categories.map((cat) => (
              <label
                key={cat.key}
                className="flex items-start gap-3 p-3 rounded-lg border border-stone-200 hover:border-sage-300 cursor-pointer transition-colors"
              >
                <div className="flex h-5 items-center pt-0.5">
                  <input
                    type="checkbox"
                    checked={prefs.categories[cat.key]}
                    onChange={() => toggleCategory(cat.key)}
                    className="h-4 w-4 rounded border-stone-300 text-sage-600 focus:ring-sage-500"
                  />
                </div>
                <div>
                  <p className="text-sm font-medium text-stone-900">{cat.label}</p>
                  <p className="text-xs text-stone-500 mt-0.5">{cat.description}</p>
                </div>
              </label>
            ))}
          </div>

          {/* Frequency */}
          <div className="space-y-3 mb-8">
            <h2 className="text-sm font-semibold text-stone-700 uppercase tracking-wide">
              {t('frequencyHeading')}
            </h2>
            {frequencies.map((freq) => (
              <label
                key={freq.value}
                className={`flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                  prefs.frequency === freq.value
                    ? 'border-sage-500 bg-sage-50'
                    : 'border-stone-200 hover:border-sage-300'
                }`}
              >
                <div className="flex h-5 items-center pt-0.5">
                  <input
                    type="radio"
                    name="frequency"
                    checked={prefs.frequency === freq.value}
                    onChange={() => {
                      setPrefs((prev) => ({ ...prev, frequency: freq.value }));
                      setSaved(false);
                    }}
                    className="h-4 w-4 border-stone-300 text-sage-600 focus:ring-sage-500"
                  />
                </div>
                <div>
                  <p className="text-sm font-medium text-stone-900">{freq.label}</p>
                  <p className="text-xs text-stone-500 mt-0.5">{freq.description}</p>
                </div>
              </label>
            ))}
          </div>

          {/* Error */}
          {error && (
            <div className="mb-4 p-3 rounded-lg bg-red-50 text-red-700 text-sm flex items-center gap-2">
              <AlertCircle className="h-4 w-4 shrink-0" />
              {error}
            </div>
          )}

          {/* Save button */}
          <button
            onClick={handleSave}
            disabled={saving}
            className="w-full py-3 rounded-lg bg-sage-700 text-white font-semibold hover:bg-sage-800 disabled:opacity-50 transition-colors flex items-center justify-center gap-2"
          >
            {saving ? (
              <>
                <span className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full" />
                {t('saving')}
              </>
            ) : saved ? (
              <>
                <Check className="h-4 w-4" />
                {t('saved')}
              </>
            ) : (
              t('save')
            )}
          </button>

          {/* Unsubscribe link */}
          <div className="mt-6 pt-4 border-t border-stone-200 text-center">
            <button
              onClick={() => {
                if (confirm(t('unsubscribeConfirm'))) {
                  const unsubPrefs = {
                    categories: { product_updates: false, growing_tips: false, promotions: false, seasonal: false },
                    frequency: 'minimal' as const,
                  };
                  fetch(`${apiBase}/api/newsletter/preferences`, {
                    method: 'PUT',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ email, token, preferences: unsubPrefs }),
                  }).then(() => {
                    setPrefs(unsubPrefs);
                    setSaved(true);
                  });
                }
              }}
              className="text-sm text-stone-500 hover:text-stone-700 underline"
            >
              {t('unsubscribeAll')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
